import { useMemo } from 'react'

import {
  estimateTextOnPathCapacity,
  measureAverageAdvancePt,
} from '@/lib/shapes/text-on-path-capacity'
import { TEXT_ON_PATH_FALLBACK_CHAR_BUDGET } from '@/lib/shapes/text-on-path-session'
import type { TextOnPathConfig } from '@/lib/shapes/types/text-on-path-config'
import { letterSpacingPt } from '@/lib/text/canvas-font-metrics'
import { useFontsOptional } from '@/providers/font'

type CharBudgetConfig = Pick<
  TextOnPathConfig,
  'fontFamilyId' | 'fontId' | 'fontSizePt' | 'tracking' | 'kerning'
>

/**
 * Second number of the text card's counter (WMPCM-12967): roughly how many characters fit along the
 * path at the current font, size, tracking and kerning. Recomputed when any of those change, when
 * the shape (and so the path length) is resized, and when the font's faces finish loading, which
 * is why it depends on the whole font context rather than a lookup function. Falls back to the
 * fixed budget until the path has been measured.
 */
export function useTextOnPathCharBudget(config: CharBudgetConfig, pathLength: number): number {
  const fonts = useFontsOptional()
  const { fontFamilyId, fontId, fontSizePt, tracking, kerning } = config

  return useMemo(() => {
    const averageAdvancePt = measureAverageAdvancePt(
      fontSizePt,
      fontFamilyId,
      fontId,
      fonts ?? undefined
    )

    return (
      estimateTextOnPathCapacity({
        pathLength,
        averageAdvancePt,
        letterSpacingPt: letterSpacingPt(tracking, kerning, fontSizePt),
      }) ?? TEXT_ON_PATH_FALLBACK_CHAR_BUDGET
    )
  }, [fonts, fontFamilyId, fontId, fontSizePt, tracking, kerning, pathLength])
}
